import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  AtSign,
  Bell,
  ChevronDown,
  ChevronsLeft,
  CircleCheck,
  Copy,
  Ellipsis,
  FileText,
  Folder,
  FolderOpen,
  Hash,
  Inbox,
  Orbit,
  Layers,
  ListTodo,
  Lock,
  MessageSquareReply,
  Palette,
  Pencil,
  Plus,
  Search,
  Star,
  StarOff,
  Trash2,
  CircleDashed,
  ListChecks,
  Grid3x3,
  CalendarRange,
  Timer,
  LayoutDashboard,
  Target,
} from '@/components/ui/icons';
import { PALETTE } from '@shared/constants';
import type { Favorite, List } from '@shared/types';
import { useDb, useDbShallow, useMe, type Tables } from '@/lib/store';
import { foldersOf, listsOf, spacesOf } from '@/lib/hierarchy';
import { locationPath, useWorkspaceId } from '@/lib/nav';
import { toggleExpanded, useUi } from '@/lib/ui';
import { deleteLocation, toggleFavorite, updateFolder, updateList, updateSpace, createDoc, moveHierarchyItem } from '@/lib/actions';
import { useIsGuest } from '@/lib/roles';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuSeparator } from '@/components/ui/Menu';
import { openContextMenu, toast } from '@/components/ui/overlays';
import { Avatar } from '@/components/ui/Avatar';
import { SidebarItem, SidebarSection, type DragItem, type DropPos } from './SidebarItem';
import { SpaceAvatar, WorkspaceLogo } from './WorkspaceLogo';
import { openDialog } from './dialogs';
import { useRail, useInboxUnread } from './Rail';
import { ChannelSections, channelTitle, unreadBadge } from './ChannelSections';
import { useActivityUnread } from '@/pages/chat/activity';

// ---------------------------------------------------------------------------
// Location context menus

type Loc = { type: 'space' | 'folder' | 'list'; id: string };

function LocationMenu({ loc, close }: { loc: Loc; close: () => void }) {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const entity = useDb((db) => (loc.type === 'space' ? db.spaces[loc.id] : loc.type === 'folder' ? db.folders[loc.id] : db.lists[loc.id]));
  const isFav = useDb((db) => Object.values(db.favorites).some((f) => f.userId === me && f.target.type === loc.type && f.target.id === loc.id));
  const guest = useIsGuest();
  if (!entity) return null;
  const copyLink = () => {
    void navigator.clipboard.writeText(location.origin + locationPath(ws, loc));
    toast('Link copied');
    close();
  };
  // guests can't create, rename or delete spaces, folders and lists
  if (guest)
    return (
      <Menu style={{ width: 240 }}>
        <MenuItem icon={<Copy size={15} />} label="Copy link" onClick={copyLink} />
        <MenuItem icon={isFav ? <StarOff size={15} /> : <Star size={15} />} label={isFav ? 'Remove from favorites' : 'Add to favorites'} onClick={() => (toggleFavorite(loc), close())} />
      </Menu>
    );
  const c = loc.type === 'space' ? 'spaces' : loc.type === 'folder' ? 'folders' : 'lists';
  const spaceId = loc.type === 'space' ? loc.id : (entity as { spaceId: string }).spaceId;
  const setColor = (color: string) => (loc.type === 'space' ? updateSpace(loc.id, { color }) : loc.type === 'folder' ? updateFolder(loc.id, { color }) : updateList(loc.id, { color }));
  return (
    <Menu style={{ width: 240 }}>
      {loc.type !== 'list' && (
        <MenuItem
          icon={<Plus size={15} />}
          label="Create new"
          submenu={(c2) => (
            <Menu>
              <MenuItem icon={<ListTodo size={15} />} label="List" onClick={() => (c2(), close(), openDialog({ kind: 'list', spaceId, folderId: loc.type === 'folder' ? loc.id : null }))} />
              {loc.type === 'space' && <MenuItem icon={<Folder size={15} />} label="Folder" onClick={() => (c2(), close(), openDialog({ kind: 'folder', spaceId }))} />}
              <MenuItem
                icon={<FileText size={15} />}
                label="Doc"
                onClick={() => {
                  c2();
                  close();
                  const { doc } = createDoc(ws, loc);
                  navigate(`/${ws}/docs/${doc.id}`);
                }}
              />
            </Menu>
          )}
        />
      )}
      <MenuItem icon={<Pencil size={15} />} label="Rename" onClick={() => (close(), openDialog({ kind: 'rename', c, id: loc.id, name: (entity as { name: string }).name }))} />
      <MenuItem icon={<Copy size={15} />} label="Copy link" onClick={copyLink} />
      <MenuSeparator />
      <MenuItem
        icon={<Palette size={15} />}
        label="Color"
        submenu={() => (
          <div className="menu" style={{ padding: 10 }}>
            <div className="swatches">
              {PALETTE.map((col) => (
                <button key={col} className="swatch" style={{ background: col }} onClick={() => setColor(col)} />
              ))}
            </div>
          </div>
        )}
      />
      <MenuItem icon={<CircleDashed size={15} />} label="Task statuses" onClick={() => (close(), openDialog({ kind: 'statuses', target: loc }))} />
      <MenuItem icon={isFav ? <StarOff size={15} /> : <Star size={15} />} label={isFav ? 'Remove from favorites' : 'Add to favorites'} onClick={() => (toggleFavorite(loc), close())} />
      <MenuSeparator />
      <MenuItem
        icon={<Trash2 size={15} />}
        label="Delete"
        danger
        onClick={() => {
          close();
          if (confirm(`Delete "${(entity as { name: string }).name}" and everything in it? This cannot be undone.`)) deleteLocation(loc);
        }}
      />
    </Menu>
  );
}

function LocationActions({ loc }: { loc: Loc }) {
  const spaceId = useDb((db) => (loc.type === 'space' ? loc.id : loc.type === 'folder' ? db.folders[loc.id]?.spaceId : db.lists[loc.id]?.spaceId));
  const guest = useIsGuest();
  return (
    <>
      <Popover placement="bottom-start" content={(close) => <LocationMenu loc={loc} close={close} />}>
        <button className="icon-btn xs">
          <Ellipsis size={14} />
        </button>
      </Popover>
      {loc.type !== 'list' && spaceId && !guest && (
        <Popover
          placement="bottom-start"
          content={(close) => (
            <Menu>
              <MenuItem icon={<ListTodo size={15} />} label="List" onClick={() => (close(), openDialog({ kind: 'list', spaceId, folderId: loc.type === 'folder' ? loc.id : null }))} />
              {loc.type === 'space' && <MenuItem icon={<Folder size={15} />} label="Folder" onClick={() => (close(), openDialog({ kind: 'folder', spaceId }))} />}
            </Menu>
          )}
        >
          <button className="icon-btn xs">
            <Plus size={14} />
          </button>
        </Popover>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Spaces tree

function useOpenCount() {
  // tasks per list that are not closed (shown next to list names)
  return useDb((db) => {
    const counts: Record<string, number> = {};
    for (const t of Object.values(db.tasks)) {
      if (t.archived || t.parentId) continue;
      counts[t.listId] = (counts[t.listId] ?? 0) + 1;
    }
    return counts;
  });
}

function ListRow({ list, depth, counts, activePath }: { list: List; depth: number; counts: Record<string, number>; activePath: string }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const path = locationPath(ws, { type: 'list', id: list.id });
  const selected = activePath === path || activePath.startsWith(path + '/');
  return (
    <SidebarItem
      depth={depth}
      icon={<ListTodo size={16} color={list.color ?? undefined} />}
      label={list.name}
      count={counts[list.id]}
      selected={selected}
      onClick={() => navigate(path)}
      onContextMenu={(e) => openContextMenu(e, (close) => <LocationMenu loc={{ type: 'list', id: list.id }} close={close} />)}
      actions={<LocationActions loc={{ type: 'list', id: list.id }} />}
      dnd={{
        item: { type: 'list', id: list.id },
        accepts: (d) => (d.type === 'list' ? ['before', 'after'] : []),
        onDrop: (d, pos) =>
          moveHierarchyItem(d, { spaceId: list.spaceId, folderId: list.folderId, beforeId: pos === 'before' ? list.id : null, afterId: pos === 'after' ? list.id : null }),
      }}
    />
  );
}

function spaceDnd(spaceId: string) {
  return {
    item: { type: 'space' as const, id: spaceId },
    accepts: (d: DragItem): DropPos[] => (d.type === 'space' ? ['before', 'after'] : ['inside']),
    onDrop: (d: DragItem, pos: DropPos) => {
      if (d.type === 'space') moveHierarchyItem(d, { beforeId: pos === 'before' ? spaceId : null, afterId: pos === 'after' ? spaceId : null });
      else if (d.type === 'folder') moveHierarchyItem(d, { spaceId });
      else {
        moveHierarchyItem(d, { spaceId, folderId: null });
        toggleExpanded(`space:${spaceId}`, true);
      }
    },
  };
}

function folderDnd(folderId: string, spaceId: string) {
  return {
    item: { type: 'folder' as const, id: folderId },
    accepts: (d: DragItem): DropPos[] => (d.type === 'folder' ? ['before', 'after'] : d.type === 'list' ? ['inside'] : []),
    onDrop: (d: DragItem, pos: DropPos) => {
      if (d.type === 'folder') moveHierarchyItem(d, { spaceId, beforeId: pos === 'before' ? folderId : null, afterId: pos === 'after' ? folderId : null });
      else {
        moveHierarchyItem(d, { spaceId, folderId });
        toggleExpanded(`folder:${folderId}`, true);
      }
    },
  };
}

export function SpacesTree() {
  const ws = useWorkspaceId();
  const guest = useIsGuest();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const db = useDbShallow((d) => ({ spaces: d.spaces, folders: d.folders, lists: d.lists })) as unknown as Tables;
  const expanded = useUi((s) => s.expanded);
  const counts = useOpenCount();
  const spaces = useMemo(() => spacesOf(db, ws), [db, ws]);
  const everything = `/${ws}/everything`;
  return (
    <div className="sb-tree">
      <SidebarItem
        icon={<Layers size={16} />}
        label={
          <>
            All Tasks <span className="faint">· {useDbName(ws)}</span>
          </>
        }
        selected={pathname.startsWith(everything)}
        onClick={() => navigate(everything)}
      />
      {spaces.map((s) => {
        const key = `space:${s.id}`;
        const open = expanded[key] ?? false;
        const spacePath = locationPath(ws, { type: 'space', id: s.id });
        return (
          <div key={s.id}>
            <SidebarItem
              icon={<SpaceAvatar spaceId={s.id} size={20} />}
              label={
                <span className="row gap-4">
                  <span className="truncate">{s.name}</span>
                  {s.private && <Lock size={11} className="faint" />}
                </span>
              }
              expandable
              expanded={open}
              onToggle={() => toggleExpanded(key)}
              selected={pathname === spacePath || pathname.startsWith(spacePath + '/')}
              onClick={() => {
                toggleExpanded(key, true);
                navigate(spacePath);
              }}
              onContextMenu={(e) => openContextMenu(e, (close) => <LocationMenu loc={{ type: 'space', id: s.id }} close={close} />)}
              actions={<LocationActions loc={{ type: 'space', id: s.id }} />}
              dnd={spaceDnd(s.id)}
            />
            {open && (
              <>
                {foldersOf(db, s.id).map((f) => {
                  const fkey = `folder:${f.id}`;
                  const fopen = expanded[fkey] ?? false;
                  const fpath = locationPath(ws, { type: 'folder', id: f.id });
                  return (
                    <div key={f.id}>
                      <SidebarItem
                        depth={1}
                        icon={fopen ? <FolderOpen size={16} color={f.color ?? undefined} /> : <Folder size={16} color={f.color ?? undefined} />}
                        label={f.name}
                        expandable
                        expanded={fopen}
                        onToggle={() => toggleExpanded(fkey)}
                        selected={pathname === fpath || pathname.startsWith(fpath + '/')}
                        onClick={() => {
                          toggleExpanded(fkey, true);
                          navigate(fpath);
                        }}
                        onContextMenu={(e) => openContextMenu(e, (close) => <LocationMenu loc={{ type: 'folder', id: f.id }} close={close} />)}
                        actions={<LocationActions loc={{ type: 'folder', id: f.id }} />}
                        dnd={folderDnd(f.id, s.id)}
                      />
                      {fopen && listsOf(db, s.id, f.id).map((l) => <ListRow key={l.id} list={l} depth={2} counts={counts} activePath={pathname} />)}
                    </div>
                  );
                })}
                {listsOf(db, s.id, null).map((l) => (
                  <ListRow key={l.id} list={l} depth={1} counts={counts} activePath={pathname} />
                ))}
              </>
            )}
          </div>
        );
      })}
      {!guest && <SidebarItem icon={<Plus size={16} />} label="New Space" muted onClick={() => openDialog({ kind: 'space' })} />}
    </div>
  );
}

function useDbName(ws: string) {
  return useDb((db) => db.workspaces[ws]?.name ?? '');
}

// ---------------------------------------------------------------------------
// Favorites

function FavoriteRow({ fav }: { fav: Favorite }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const db = useDb((d) => d);
  const t = fav.target;
  let icon: React.ReactNode = <Star size={16} />;
  let label = '';
  let path = '';
  if (t.type === 'space') {
    icon = <SpaceAvatar spaceId={t.id} size={20} />;
    label = db.spaces[t.id]?.name ?? '';
    path = locationPath(ws, { type: 'space', id: t.id });
  } else if (t.type === 'folder') {
    icon = <Folder size={16} />;
    label = db.folders[t.id]?.name ?? '';
    path = locationPath(ws, { type: 'folder', id: t.id });
  } else if (t.type === 'list') {
    icon = <ListTodo size={16} />;
    label = db.lists[t.id]?.name ?? '';
    path = locationPath(ws, { type: 'list', id: t.id });
  } else if (t.type === 'task') {
    icon = <CircleCheck size={16} />;
    label = db.tasks[t.id]?.name ?? '';
    path = `${pathname}?t=${t.id}`;
  } else if (t.type === 'doc') {
    icon = <FileText size={16} />;
    label = db.docs[t.id]?.title ?? '';
    path = `/${ws}/docs/${t.id}`;
  } else if (t.type === 'channel') {
    const ch = db.channels[t.id];
    icon = <Hash size={16} />;
    label = ch ? (ch.kind === 'channel' ? ch.name : channelTitle(db, ch, fav.userId)) : '';
    path = `/${ws}/chat/${t.id}`;
  }
  if (!label) return null;
  return (
    <SidebarItem
      icon={icon}
      label={label}
      selected={pathname === path}
      onClick={() => navigate(path)}
      onContextMenu={(e) =>
        openContextMenu(e, (close) => (
          <Menu>
            <MenuItem icon={<StarOff size={15} />} label="Remove from favorites" onClick={() => (toggleFavorite(t), close())} />
          </Menu>
        ))
      }
    />
  );
}

function Favorites() {
  const me = useMe();
  const favs = useDb((db) => db.favorites);
  const list = useMemo(() => Object.values(favs).filter((f) => f.userId === me).sort((a, b) => a.orderIndex - b.orderIndex), [favs, me]);
  const open = useUi((s) => s.expanded['sec:favorites'] ?? true);
  if (!list.length) return null;
  return (
    <SidebarSection title="Favorites" icon={<Star size={14} />} open={open} onToggle={() => toggleExpanded('sec:favorites', !open)}>
      {list.map((f) => (
        <FavoriteRow key={f.id} fav={f} />
      ))}
    </SidebarSection>
  );
}

// ---------------------------------------------------------------------------
// Sidebars

function CreateMenu({ close }: { close: () => void }) {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const firstSpace = useDb((db) => spacesOf(db, ws)[0]?.id);
  const guest = useIsGuest();
  // a personal group for the Chat sidebar (anyone, guests included)
  const section = <MenuItem icon={<Folder size={15} />} label="Chat section" onClick={() => (close(), openDialog({ kind: 'chat-section', workspaceId: ws }))} />;
  if (guest)
    return (
      <Menu style={{ width: 220 }}>
        <MenuItem icon={<CircleCheck size={15} />} label="Task" onClick={() => (close(), useUi.setState({ createTaskOpen: true }))} />
        {section}
      </Menu>
    );
  return (
    <Menu style={{ width: 220 }}>
      <MenuItem icon={<CircleCheck size={15} />} label="Task" onClick={() => (close(), useUi.setState({ createTaskOpen: true }))} />
      <MenuItem icon={<ListTodo size={15} />} label="List" disabled={!firstSpace} onClick={() => (close(), firstSpace && openDialog({ kind: 'list', spaceId: firstSpace, folderId: null }))} />
      <MenuItem
        icon={<FileText size={15} />}
        label="Doc"
        onClick={() => {
          close();
          const { doc } = createDoc(ws, null);
          navigate(`/${ws}/docs/${doc.id}`);
        }}
      />
      <MenuItem icon={<Hash size={15} />} label="Channel" onClick={() => (close(), navigate(`/${ws}/chat?new=channel`))} />
      {section}
      <MenuSeparator />
      <MenuItem icon={<Grid3x3 size={15} />} label="Space" onClick={() => (close(), openDialog({ kind: 'space' }))} />
    </Menu>
  );
}

function SidebarHeader({ title, extra }: { title: string; extra?: React.ReactNode }) {
  return (
    <div className="sb-header">
      <span className="sb-header-title">{title}</span>
      <span className="spacer" />
      {extra}
      <button className="icon-btn sm sb-collapse" title="Collapse sidebar ( [ )" onClick={() => useUi.setState({ sidebarCollapsed: true })}>
        <ChevronsLeft size={15} />
      </button>
      <Popover placement="bottom-end" content={(close) => <CreateMenu close={close} />}>
        <button className="sb-create">
          <Plus size={15} />
          <ChevronDown size={12} />
        </button>
      </Popover>
    </div>
  );
}

function HomeNav() {
  const ws = useWorkspaceId();
  const me = useMe();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unread = useInboxUnread();
  const myOpen = useDb((db) => {
    let n = 0;
    for (const t of Object.values(db.tasks)) if (!t.archived && !t.closedAt && t.assigneeIds.includes(me)) n++;
    return n;
  });
  const assignedComments = useDb((db) => Object.values(db.comments).filter((c) => c.assigneeId === me && !c.resolved).length);
  const item = (path: string, icon: React.ReactNode, label: string, count?: number | string, badge?: React.ReactNode) => (
    <SidebarItem icon={icon} label={label} count={count} badge={badge} selected={pathname === `/${ws}/${path}`} onClick={() => navigate(`/${ws}/${path}`)} />
  );
  const moreOpen = useUi((s) => s.expanded['home:more'] ?? false);
  return (
    <div className="sb-nav">
      {item('inbox', <Inbox size={16} />, 'Inbox', undefined, unread > 0 ? <span className="sb-badge">{unread > 99 ? '99+' : unread}</span> : undefined)}
      {item('replies', <MessageSquareReply size={16} />, 'Replies')}
      {item('assigned-comments', <AtSign size={16} />, 'Assigned Comments', assignedComments || undefined)}
      {item('home', <ListChecks size={16} />, 'My Tasks', myOpen || undefined)}
      {moreOpen && (
        <>
          {item('docs', <FileText size={16} />, 'Docs')}
          {item('dashboards', <LayoutDashboard size={16} />, 'Dashboards')}
          {item('planner', <CalendarRange size={16} />, 'Planner')}
          {item('timesheets', <Timer size={16} />, 'Timesheets')}
          {item('goals', <Target size={16} />, 'Goals')}
          {item('everything', <Layers size={16} />, 'Everything')}
        </>
      )}
      <SidebarItem icon={<Ellipsis size={16} />} label={moreOpen ? 'Less' : 'More'} onClick={() => toggleExpanded('home:more', !moreOpen)} />
    </div>
  );
}

function HomeSidebar() {
  const filter = useUi((s) => s.homeFilter);
  const spacesOpen = useUi((s) => s.expanded['sec:spaces'] ?? true);
  const guest = useIsGuest();
  return (
    <>
      <SidebarHeader title="Home" />
      <div className="sb-filters">
        {(['unread', 'dms'] as const).map((f) => (
          <button key={f} className={clsx('sb-filter', { on: filter === f })} onClick={() => useUi.setState({ homeFilter: filter === f ? 'all' : f })}>
            {f === 'unread' ? 'Unread' : 'DMs'}
          </button>
        ))}
      </div>
      <div className="sb-scroll">
        {filter === 'all' && <HomeNav />}
        {filter === 'all' && <Favorites />}
        {filter === 'all' && (
          <SidebarSection
            title="Spaces"
            icon={<Orbit size={14} />}
            open={spacesOpen}
            onToggle={() => toggleExpanded('sec:spaces', !spacesOpen)}
            actions={
              !guest && (
                <button className="icon-btn xs" onClick={() => openDialog({ kind: 'space' })}>
                  <Plus size={14} />
                </button>
              )
            }
          >
            <SpacesTree />
          </SidebarSection>
        )}
        <ChannelSections filter={filter} />
      </div>
    </>
  );
}

function SpacesSidebar() {
  return (
    <>
      <SidebarHeader
        title="Spaces"
        extra={
          <button className="icon-btn sm" onClick={() => useUi.setState({ commandOpen: true })}>
            <Search size={15} />
          </button>
        }
      />
      <div className="sb-scroll">
        <Favorites />
        <div style={{ paddingTop: 4 }}>
          <SpacesTree />
        </div>
      </div>
    </>
  );
}

function ChatActivityItem() {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unread = useActivityUnread();
  return (
    <div className="sb-nav">
      <SidebarItem
        icon={<Bell size={16} />}
        label="Activity"
        selected={pathname === `/${ws}/chat/activity`}
        badge={unreadBadge(unread)}
        onClick={() => navigate(`/${ws}/chat/activity`)}
      />
    </div>
  );
}

function ChatSidebar() {
  return (
    <>
      <SidebarHeader title="Chat" />
      <div className="sb-scroll">
        <ChatActivityItem />
        <ChannelSections filter="all" />
      </div>
    </>
  );
}

export function Sidebar() {
  const section = useRail((s) => s.section);
  const ws = useWorkspaceId();
  return (
    <aside className="sidebar">
      {section === 'spaces' ? <SpacesSidebar /> : section === 'chat' ? <ChatSidebar /> : <HomeSidebar />}
      <div className="sb-footer">
        <WorkspaceLogo workspaceId={ws} size={18} />
        <span className="faint truncate" style={{ fontSize: 'var(--fs-sm)' }}>
          <MeName />
        </span>
      </div>
    </aside>
  );
}

function MeName() {
  const me = useMe();
  const name = useDb((db) => db.users[me]?.name);
  return (
    <span className="row gap-6">
      <Avatar userId={me} size={16} />
      {name}
    </span>
  );
}

